/**
 * @fileoverview Mock backend for the login endpoint.
 * @description Registers POST /identity/login on the feature's shared mock adapter (see
 * auth-mock-adapter.ts) so the real request/response interceptors still run. Checks credentials
 * against the shared mock user directory (mock-users.json's fixed users, plus anyone registered at
 * runtime by accepting an invitation — see mock-user-directory.ts) and opens a challenge (see
 * challenge-store.ts) rather than signing the user in directly — see two-factor-mock.service.ts for
 * the step that actually completes sign-in. A registered-but-not-activated user (accepted an
 * invitation, never entered the activation code) gets an activation challenge instead, with
 * nextStep VERIFY_ACTIVATION_MFA — see activation-mock.service.ts. Every distinct internal failure reason the identity
 * backend recognizes (unknown identifier, wrong password, locked/suspended/deactivated account,
 * blocked device, blocked by security policy, rate-limited, temporarily unavailable) collapses to
 * the same small set of public appCodes — SCENARIO_TRIGGERS lets each be exercised on demand via a
 * reserved test identifier, since a real backend would never let the client tell which one
 * actually happened. A no-op when VITE_ENABLE_MOCK_API is false, so switching to a real backend is
 * deleting the one import of this file in features/auth/index.ts.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { authMockAdapter } from './auth-mock-adapter'
import { createChallenge } from './challenge-store'
import { buildMockErrorEnvelope, buildMockSuccessEnvelope } from './mock-envelope.utils'
import { findMockUserByEmail } from './mock-user-directory'

import type { LoginRequest, LoginResponse } from '@/features/auth/services/auth.service'

interface ScenarioTrigger {
  httpStatus: number
  appCode: string
  message: string
}

const GENERIC_AUTH_FAILURE: ScenarioTrigger = {
  httpStatus: 401,
  appCode: '1001',
  message: 'Authentication failed',
}

/**
 * @description Reserved test identifiers that let every internal failure reason in the identity
 * backend's appCode table be exercised on demand, regardless of the password entered. Case/trim
 * insensitive, matching findMockUserByEmail's own lookup.
 */
const SCENARIO_TRIGGERS: Record<string, ScenarioTrigger> = {
  'inactive@insightbox.tech': GENERIC_AUTH_FAILURE,
  'locked@insightbox.tech': GENERIC_AUTH_FAILURE,
  'suspended@insightbox.tech': GENERIC_AUTH_FAILURE,
  'deactivated@insightbox.tech': GENERIC_AUTH_FAILURE,
  'deviceblocked@insightbox.tech': GENERIC_AUTH_FAILURE,
  'securityblocked@insightbox.tech': GENERIC_AUTH_FAILURE,
  'ratelimited@insightbox.tech': {
    httpStatus: 429,
    appCode: '1002',
    message: 'Too many login attempts. Please try again later.',
  },
  'unavailable@insightbox.tech': {
    httpStatus: 503,
    appCode: '1003',
    message: 'Authentication is temporarily unavailable. Please try again.',
  },
}

/**
 * @description Masks all but an email's first character and domain, e.g. "a***@example.com".
 * @param {string} email - The email address to mask.
 * @returns {string} The masked email address.
 */
function maskEmail(email: string): string {
  const [localPart, domain] = email.split('@')

  if (!localPart || !domain) {
    return email
  }

  return `${localPart[0]}***@${domain}`
}

if (authMockAdapter) {
  authMockAdapter.onPost('/identity/login').reply((config) => {
    const { identifier, password } = JSON.parse(config.data as string) as LoginRequest
    const normalizedIdentifier = identifier.trim().toLowerCase()
    const trigger = SCENARIO_TRIGGERS[normalizedIdentifier]

    if (trigger) {
      return [
        trigger.httpStatus,
        buildMockErrorEnvelope(trigger.httpStatus, trigger.appCode, trigger.message),
      ]
    }

    const candidate = findMockUserByEmail(identifier)
    const match = candidate?.password === password ? candidate : undefined

    if (!match) {
      return [
        GENERIC_AUTH_FAILURE.httpStatus,
        buildMockErrorEnvelope(
          GENERIC_AUTH_FAILURE.httpStatus,
          GENERIC_AUTH_FAILURE.appCode,
          GENERIC_AUTH_FAILURE.message,
        ),
      ]
    }

    const needsActivation = match.accountStatus === 'REGISTERED'
    const challenge = createChallenge(match.id, needsActivation ? 'ACTIVATION' : 'LOGIN')

    const response: LoginResponse = {
      loginStatus: 'IN_PROGRESS',
      mfaRequired: true,
      challengeId: challenge.challengeToken,
      channel: 'EMAIL',
      destinationMasked: maskEmail(match.email),
      expiresAt: new Date(challenge.expiresAt).toISOString(),
      resendAvailableAt: new Date(challenge.resendAvailableAt).toISOString(),
      nextStep: needsActivation ? 'VERIFY_ACTIVATION_MFA' : 'VERIFY_LOGIN_MFA',
    }

    return [200, buildMockSuccessEnvelope(response, 'MFA verification required')]
  })
}
